import { supabase } from '@/integrations/supabase/client';

/** Shape of a voice as returned by the ElevenLabs /v1/voices endpoint. */
export interface ElevenLabsVoice {
  voice_id: string;
  name: string;
  labels?: Record<string, string>;
  [key: string]: unknown;
}

export const VOICES_CACHE_KEY = 'elevenlabs_voices';

/**
 * supabase.functions.invoke() rejects with a FunctionsHttpError whose message is
 * a generic "non-2xx status code". The useful message lives in the response body,
 * which is only reachable through the error's `context`. Without this, every
 * server-side failure reason (bad key, quota, missing permissions) is lost.
 */
async function extractFunctionError(error: unknown): Promise<string | null> {
  const context = (error as { context?: unknown })?.context;
  if (!(context instanceof Response)) return null;

  try {
    const body = await context.clone().json();
    if (typeof body?.error === 'string') return body.error;
  } catch {
    // Body was not JSON, or was already consumed; fall through.
  }
  return null;
}

/**
 * Loads the current user's ElevenLabs voices through the get-elevenlabs-voices
 * edge function.
 *
 * The fetch deliberately runs server-side: calling api.elevenlabs.io from the
 * browser would require shipping the user's decrypted API key to the client.
 *
 * Throws with the server's own message when the request fails. Returns an empty
 * array only when the account genuinely has no voices - callers must not cache
 * that, or a retry becomes impossible for the rest of the session.
 */
export async function fetchElevenLabsVoices(): Promise<ElevenLabsVoice[]> {
  const { data, error } = await supabase.functions.invoke(
    'get-elevenlabs-voices',
  );

  if (error) {
    const detail = await extractFunctionError(error);
    throw new Error(detail || error.message || 'Failed to load voices.');
  }

  // The function returns 200 with { success: false } for some handled failures.
  if (data && data.success === false) {
    throw new Error(data.error || 'Failed to load voices.');
  }

  return (data?.voices as ElevenLabsVoice[]) || [];
}

/** Reads cached voices, treating an empty or malformed list as a cache miss. */
export function readCachedVoices(): ElevenLabsVoice[] | null {
  const cached = sessionStorage.getItem(VOICES_CACHE_KEY);
  if (!cached) return null;

  try {
    const parsed = JSON.parse(cached);
    if (Array.isArray(parsed) && parsed.length > 0) return parsed;
  } catch {
    // Malformed cache; fall through to clear it.
  }

  sessionStorage.removeItem(VOICES_CACHE_KEY);
  return null;
}

/** Caches a voice list. Empty lists clear the cache instead of being stored. */
export function writeCachedVoices(voices: ElevenLabsVoice[]): void {
  if (voices.length > 0) {
    sessionStorage.setItem(VOICES_CACHE_KEY, JSON.stringify(voices));
  } else {
    sessionStorage.removeItem(VOICES_CACHE_KEY);
  }
}

/**
 * Generates a spoken preview for a voice via the preview-elevenlabs-voice edge
 * function and returns a playable data URL. Server-side for the same reason as
 * fetchElevenLabsVoices: the API key must not reach the browser.
 */
export async function previewElevenLabsVoice(voiceId: string): Promise<string> {
  const { data, error } = await supabase.functions.invoke(
    'preview-elevenlabs-voice',
    { body: { voiceId } },
  );

  if (error) {
    const detail = await extractFunctionError(error);
    throw new Error(detail || error.message || 'Failed to preview voice.');
  }

  if (!data?.success || !data?.audioUrl) {
    throw new Error(data?.error || 'Failed to generate voice preview.');
  }

  return data.audioUrl as string;
}

export const EMPTY_VOICES_MESSAGE =
  'Your ElevenLabs account returned no voices. Add a voice to your ElevenLabs Voice Library, then try again.';
